import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { COMPLIANT_LABEL, NON_COMPLIANT_LABEL, NA_LABEL, FIXED_COMPLIANT_LABEL, PARTIALLY_COMPLIANT_LABEL } from "src/constants/constants";
import { Button, Column, Icon, Loader, Row, Sheet, Text } from "@amzn/meridian";
import closeSmallTokens from "@amzn/meridian-tokens/base/icon/close-small";
import { InspectionResult, isOverridden, isFixedNow, isFixedLater, isVerified, resolveDisplayedComment } from "src/services/inspectionService";
import { formatAttestationDateTime } from "src/utils/dateUtil";
import "./ComplianceDetailSheet.scss";
import ThemedTag from "./ThemedTag";
import OverrideIcon from "./OverrideIcon";
import EvidenceTabs from "./EvidenceTabs";
import { isResultNA } from "src/components/pages/inspection/utils";

/**
 * Returns true when a result's confirmedResult is a numeric value (e.g. "4")
 * rather than a boolean-style "Yes"/"No". Auto-calculated results can produce
 * numeric confirmed values that should be displayed as-is.
 */
function isNumericResult(result: InspectionResult): boolean {
    return result.confirmedResult !== null
        && result.confirmedResult !== undefined
        && result.confirmedResult !== ""
        && !isNaN(Number(result.confirmedResult));
}

interface ComplianceDetailSheetProps {
    open: boolean;
    onClose: () => void;
    result: InspectionResult | null;
    presignedUrlMap: Record<string, string>;
    onOverride?: (result: InspectionResult) => void;
    /** Fix-it-now: only offered for non-compliant results. */
    onFixNow?: (result: InspectionResult) => void;
    /** Fix-it-later: only offered for non-compliant results not already deferred. */
    onFixLater?: (result: InspectionResult) => void;
    /** Human-readable question text from the workflow question config. */
    questionText?: string;
    /** Whether the question was answered by AI. Derived from the workflow question. */
    isAI?: boolean;
    /** Whether the question is auto-calculated. Derived from the workflow question mode. */
    isAutoCalculated?: boolean;
}

const ComplianceDetailSheet: React.FC<ComplianceDetailSheetProps> = ({
    open,
    onClose,
    result,
    presignedUrlMap,
    onOverride,
    onFixNow,
    onFixLater,
    questionText,
    isAI = false,
    isAutoCalculated = false,
}) => {
    const [expandedUrl, setExpandedUrl] = useState<string | null>(null);
    const [loadedThumbnails, setLoadedThumbnails] = useState<Record<string, boolean>>({});
    const [activeTab, setActiveTab] = useState<"overridden" | "original">("overridden");

    // Close the portaled lightbox when the sheet closes or switches to a
    // different result, otherwise it can stay orphaned on screen / show a stale image.
    useEffect(() => {
        setExpandedUrl(null);
        setLoadedThumbnails({});
        setActiveTab("overridden");
    }, [open, result?.objectId]);

    if (!result) return null;

    const fixedNow = isFixedNow(result);
    const fixedLater = isFixedLater(result);
    const fixEvidences = result.complianceFixDetails?.evidences ?? [];

    // Fall back to fix photos when tabs are suppressed, so fix evidence is never dropped.
    const evidenceUrls = (fixedNow ? fixEvidences : result.evidence)
        .map((e) => presignedUrlMap[e.uri])
        .filter(Boolean);

    const shouldShowTabs = fixedNow
        ? fixEvidences.length > 0 && result.evidence.length > 0
        : isOverridden(result) && result.evidence.length > 0;

    const isAnalysisFailed = result.aiResult?.toUpperCase() === "FAILED";
    const isAnalysisPending = result.aiResult?.toUpperCase() === "PENDING";
    const isNA = isResultNA(result);

    const isOriginalTabActive = shouldShowTabs && activeTab === "original";

    // On the original tab of an overridden result, show the pre-override verdict.
    // A fixed-now result was always originally non-compliant.
    const displayCompliant = fixedNow
        ? !isOriginalTabActive
        : (isOriginalTabActive && result.originalIsCompliant !== undefined ? result.originalIsCompliant : result.isCompliant);

    const themedTagText = isAnalysisPending ? "Analyzing..."
        : isAnalysisFailed ? "Failed"
            : isNA ? NA_LABEL
                : fixedNow && !isOriginalTabActive ? FIXED_COMPLIANT_LABEL
                    : fixedLater && !isOriginalTabActive ? PARTIALLY_COMPLIANT_LABEL
                        : displayCompliant ? COMPLIANT_LABEL
                            : NON_COMPLIANT_LABEL;

    const themedTagType = isAnalysisPending ? "neutral"
        : isNA ? "gray"
            : fixedNow && !isOriginalTabActive ? "fixCompliant"
                : fixedLater && !isOriginalTabActive ? "fixLater"
                    : displayCompliant ? "success"
                        : "error";

    // A verified checkpoint locks its answer: no override until it's un-verified.
    const canOverride = !isAnalysisPending && !isAnalysisFailed && !isNA && !fixedNow && !fixedLater && !isVerified(result);

    // Answer source, mirroring the tag on ReviewCheckpointCard so the sheet and the
    // card never disagree. Both values come from the workflow question config.
    const sourceTagText = (isAutoCalculated ? "Auto" : isAI ? "AI" : "Manual").toUpperCase();

    const commentText = fixedLater
        ? result.complianceFixDetails?.comment?.trim()
        : fixedNow
            ? (isOriginalTabActive ? result.userComment : result.complianceFixDetails?.comment?.trim())
            : resolveDisplayedComment(
                isOverridden(result), isOriginalTabActive, result.overrideReason, result.userComment
            );

    return (
        <>
        <Sheet type="overlay" side="bottom" open={open} onClose={onClose} className="compliance-detail-sheet">
            <Column spacing="400" spacingInset="500">
                <Row alignmentHorizontal="justify" alignmentVertical="center">
                    <Text type="h300">Compliance Details</Text>
                    <Button onClick={onClose} type="icon">
                        <Icon tokens={closeSmallTokens}>Close compliance details</Icon>
                    </Button>
                </Row>

                {questionText && (
                    <Text type="b200" color="secondary">
                        {questionText}
                    </Text>
                )}

                {result.objectId !== null && (
                    <Text type="h200" color="primary">
                        {result.objectId}
                    </Text>
                )}

                <Row alignmentHorizontal="justify" alignmentVertical="center">
                    <Text type="b200" color="primary">
                        Answer : {isNumericResult(result) ? result.confirmedResult : displayCompliant ? "YES" : "NO"}
                    </Text>
                    <Row spacing="200" alignmentVertical="center">
                        <ThemedTag text={themedTagText} type={themedTagType} />
                        {/* Answer source tag is only meaningful once analysis has settled. */}
                        {!isAnalysisPending && <ThemedTag text={sourceTagText} type="neutral" />}
                    </Row>
                </Row>

                {!fixedNow && isOverridden(result) && activeTab !== "original" && (
                    <div style={{ alignSelf: 'flex-start' }}>
                        <ThemedTag text="Manually Overridden" type="override" icon={<OverrideIcon />} />
                    </div>
                )}


                {isAnalysisPending ? (
                    <Loader type="circular" size="large" />
                ) : (
                    <>
                        {(!shouldShowTabs || activeTab === "original") && result.aiReasoning && result.aiReasoning !== "" && (
                            <Column spacing="100">
                                <Text type="b100" color="secondary">Reason</Text>
                                <Text type="b200" color="primary">{result.aiReasoning}</Text>
                            </Column>
                        )}

                        {fixedLater && commentText && (
                            <Column spacing="100">
                                <Text type="b100" color="secondary">Reason Deferred</Text>
                                <Text type="b200" color="primary">{commentText}</Text>
                            </Column>
                        )}

                        {shouldShowTabs ? (
                            <EvidenceTabs
                                activeTab={activeTab}
                                onTabChange={setActiveTab}
                                overriddenEvidences={fixedNow ? fixEvidences : (result.overriddenEvidences ?? [])}
                                originalEvidences={result.evidence}
                                presignedUrlMap={presignedUrlMap}
                                onImageExpand={setExpandedUrl}
                                firstTabLabel={fixedNow ? "Fix Evidence" : undefined}
                            />
                        ) : evidenceUrls.length > 0 && (
                            <Row spacing="300" wrap="down">
                                {evidenceUrls.map((url, idx) => (
                                    <button
                                        key={idx}
                                        type="button"
                                        className="compliance-detail-sheet__image-button"
                                        onClick={() => setExpandedUrl(url)}
                                        aria-label={`Expand evidence ${idx + 1}`}
                                    >
                                        <img
                                            src={url}
                                            alt={`Evidence ${idx + 1}`}
                                            className={`compliance-detail-sheet__image${evidenceUrls.length === 1 ? " compliance-detail-sheet__image--large" : ""}`}
                                            onLoad={() => setLoadedThumbnails((prev) => ({ ...prev, [url]: true }))}
                                        />
                                        {loadedThumbnails[url] && (
                                            <span className="compliance-detail-sheet__image-hint">
                                                <span aria-hidden="true" className="compliance-detail-sheet__image-hint-icon">⤢</span>
                                                Expand
                                            </span>
                                        )}
                                    </button>
                                ))}
                            </Row>
                        )}

                        {!fixedLater && commentText && (
                            <Column spacing="100">
                                <Text type="h200" color="primary">Comments</Text>
                                <Text type="b200" color="secondary">{commentText}</Text>
                            </Column>
                        )}

                        {isVerified(result) && result.verifiedAt && (
                            <Text type="b100" color="secondary">
                                Attested by {result.verifiedBy} at {formatAttestationDateTime(result.verifiedAt)}
                            </Text>
                        )}

                        {canOverride && !isOriginalTabActive && (onFixNow || onOverride || onFixLater) && (
                            <Column spacing="300">
                                {onFixNow && !result.isCompliant && (
                                    <button
                                        className="compliance-detail-sheet__fix-now-btn"
                                        onClick={() => onFixNow(result)}
                                    >
                                        FIX IT NOW
                                    </button>
                                )}

                                <Row spacing="300" widths="fill">
                                    {onOverride && (
                                        <button
                                            className="compliance-detail-sheet__override-btn"
                                            onClick={() => onOverride(result)}
                                        >
                                            OVERRIDE
                                        </button>
                                    )}
                                    {onFixLater && !result.isCompliant && !isFixedLater(result) && (
                                        <button
                                            className="compliance-detail-sheet__fix-later-btn"
                                            onClick={() => onFixLater(result)}
                                        >
                                            FIX LATER
                                        </button>
                                    )}
                                </Row>
                            </Column>
                        )}
                    </>
                )}
            </Column>
        </Sheet>

        {expandedUrl && ReactDOM.createPortal(
            <div
                className="compliance-detail-sheet__lightbox"
                aria-label="Expanded evidence image"
                onClick={() => setExpandedUrl(null)}
            >
                <button
                    type="button"
                    className="compliance-detail-sheet__lightbox-close"
                    onClick={() => setExpandedUrl(null)}
                    aria-label="Close expanded image"
                >
                    &times;
                </button>
                <img
                    src={expandedUrl}
                    alt="Expanded evidence"
                    className="compliance-detail-sheet__lightbox-image"
                    onClick={(e) => e.stopPropagation()}
                />
            </div>,
            document.body
        )}
        </>
    );
};

export default ComplianceDetailSheet;
